import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import { useApp } from '../context/AppContext';
import { COMMUNITIES, CHAPTERS, JOURNEY_OPTIONS, WOMEN_TO_DISCOVER, LANDING_SHOWCASE_CHAPTERS } from '../data';

function zipToCity(zip) {
  const z = String(zip || '').replace(/\D/g, '').slice(0, 5);
  const prefix3 = z.slice(0, 3);
  const map = {
    '430': 'Columbus', '431': 'Columbus', '432': 'Columbus', '433': 'Columbus',
    '021': 'Boston', '022': 'Boston', '023': 'Boston', '024': 'Newton',
    '441': 'Cleveland', '442': 'Cleveland', '443': 'Akron',
    '452': 'Cincinnati', '453': 'Dayton',
  };
  return map[prefix3] || (z ? 'your area' : '');
}

function avatarSeed(str) {
  const s = String(str || '').toLowerCase().trim();
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n += s.charCodeAt(i);
  return n;
}

function initialsFrom(nameOrIdentifier) {
  const raw = String(nameOrIdentifier || '').trim();
  if (!raw) return 'U';
  const safe = raw.replace(/[^a-zA-Z0-9\s]/g, '');
  const parts = safe.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return safe.slice(0, 1).toUpperCase();
  const letters = parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
  return letters || safe.slice(0, 1).toUpperCase();
}

function avatarStyleFromIdentifier(identifier) {
  const n = avatarSeed(identifier);
  const palette = [
    { bg: 'linear-gradient(140deg,#F4C0D1,#9FE1CB)', color: 'var(--pink-dk)' },
    { bg: 'linear-gradient(140deg,#EEEDFE,#534AB7)', color: 'var(--purple)' },
    { bg: 'linear-gradient(140deg,#E1F5EE,#0F6E56)', color: 'var(--sage-d)' },
    { bg: 'linear-gradient(140deg,#FCE8F3,#BE185D)', color: 'var(--hotpink-dk, var(--hotpink))' },
    { bg: 'linear-gradient(140deg,#E6F1FB,#185FA5)', color: 'var(--blue-dk, #0C447C)' },
  ];
  return palette[n % palette.length];
}

function chapterLabel(chapterId) {
  const id = (chapterId || '').toLowerCase();
  const mapped = id === 'fertility' ? 'ivf' : id;
  const c = CHAPTERS.find((x) => x.id === mapped || x.id === id);
  return c?.name || (id === 'ivf' ? 'Fertility' : id) || 'Your chapter';
}

/** Display chapter name from journey type. */
function chapterDisplayName(journeyType) {
  const map = {
    cycle: 'Trying to Conceive',
    ivf: 'Fertility',
    fertility: 'Fertility',
    ttc: 'Trying to Conceive',
    pregnancy: 'Pregnancy',
    postpartum: 'Motherhood',
    baby: 'Motherhood',
    menopause: 'Motherhood',
    loss: 'Fertility',
    surrogacy: 'Fertility',
  };
  return map[String(journeyType || '').toLowerCase()] || LANDING_SHOWCASE_CHAPTERS.find((c) => c.benefitKey === journeyType)?.name || 'Fertility';
}

export default function Profile() {
  const navigate = useNavigate();
  const { identifier } = useParams();
  const {
    user,
    friends,
    getSocialPosts,
    getJoinedGroupIds,
    getDiscoverProfile,
    updateDiscoverProfile,
    updateAccountProfile,
    changePassword,
    changeEmail,
    addFriend,
  } = useApp();

  const ident = String(identifier || '').toLowerCase().trim();
  const discoverProfile = useMemo(() => WOMEN_TO_DISCOVER.find((w) => w.id === ident), [ident]);
  const myProfile = getDiscoverProfile ? getDiscoverProfile(user) : {};

  const isMe = !!user?.email && String(user.email).toLowerCase().trim() === ident;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    bio: '',
    fertilityJourney: '',
    location: '',
    clinic: '',
    interests: '',
    photos: [],
    coverPhoto: '',
    birthday: '',
  });
  const [accountEditing, setAccountEditing] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (!isMe || !getDiscoverProfile) return;
    const prof = getDiscoverProfile(user);
    const city = zipToCity(user?.zip_code);
    const loc = prof.location || (city ? `${city}, OH` : '');
    setForm({
      bio: prof.bio || '',
      fertilityJourney: prof.fertilityJourney || user?.journeyType || '',
      location: loc,
      clinic: prof.clinic || '',
      interests: Array.isArray(prof.interests) ? prof.interests.join(', ') : (prof.interests || ''),
      photos: Array.isArray(prof.photos) ? prof.photos : [],
      coverPhoto: prof.coverPhoto || '',
      birthday: user?.date_of_birth || user?.birthday || '',
    });
  }, [isMe, user?.email, user?.zip_code, user?.journeyType, getDiscoverProfile]);

  useEffect(() => {
    if (isMe && user?.email) {
      setAccountForm((f) => ({ ...f, email: user.email || '' }));
    }
  }, [isMe, user?.email]);

  const posts = useMemo(() => {
    const all = getSocialPosts ? getSocialPosts() : [];
    const list = Array.isArray(all) ? all : [];
    return list.filter((p) => String(p?.userEmail || '').toLowerCase().trim() === ident);
  }, [getSocialPosts, ident]);

  const profileName = useMemo(() => {
    if (isMe) return user?.name || 'You';
    if (discoverProfile) return discoverProfile.name;
    const f = Array.isArray(friends) ? friends.find((x) => x.identifier === ident) : null;
    return f?.name || ident.split('@')[0] || 'Member';
  }, [isMe, user, friends, ident, discoverProfile]);

  const joinedGroupIds = useMemo(() => (isMe && user ? getJoinedGroupIds(user) : []), [isMe, user, getJoinedGroupIds]);
  const headerAvatar = (isMe ? myProfile?.photos?.[0] : discoverProfile?.photo) || '/images/chapters/fertility.png';
  const headerCover = (isMe ? (myProfile?.coverPhoto || myProfile?.photos?.[1] || myProfile?.photos?.[0]) : (discoverProfile?.coverPhoto || discoverProfile?.photo)) || '/images/chapters/fertility.png';
  const profileHandle = String((isMe ? user?.email : ident) || '').split('@')[0] || 'member';

  return (
    <DashboardAppShell fontFamily="'Plus Jakarta Sans', -apple-system, sans-serif">
      <div style={{ padding: 8, maxWidth: 1400, margin: '0 auto', width: '100%' }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginBottom: 18 }}>
          <div style={{ position: 'relative', height: 180, background: 'var(--sand-2)' }}>
            <img src={headerCover} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(255,252,249,0.12) 0%, rgba(255,252,249,0.5) 100%)' }} />
            <div style={{ position: 'absolute', left: 22, bottom: -52, width: 108, height: 108, borderRadius: '50%', border: '4px solid var(--white)', background: 'var(--pink-m)', overflow: 'hidden' }}>
              <img src={headerAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
          <div style={{ padding: '62px 20px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 36, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{profileName}</div>
                <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 6 }}>
                  @{profileHandle} · {chapterLabel(user?.journeyType || discoverProfile?.journey)}{discoverProfile?.location ? ` · ${discoverProfile.location}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {isMe ? (
                  <button type="button" className="btn-primary" onClick={() => setEditing(true)} style={{ borderRadius: 999, padding: '8px 18px' }}>Edit profile</button>
                ) : (
                  <button type="button" className="btn-primary" onClick={() => addFriend?.({ identifier: discoverProfile?.id || ident, name: profileName })} style={{ borderRadius: 999, padding: '8px 18px' }}>+ Add friend</button>
                )}
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() => navigate(discoverProfile ? '/connect' : '/community')}
                  style={{ fontWeight: 800, padding: '8px 14px', borderRadius: 999 }}
                >
                  ← Back
                </button>
              </div>
            </div>
            {isMe ? (
              <div style={{ fontSize: 12, color: 'var(--pink-d)', fontWeight: 700 }}>This is your profile. Private account details are below.</div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>
                {Array.isArray(friends) && friends.some((f) => f.identifier === ident) ? 'You’ve added this person' : 'Friend info is limited in this demo'}
              </div>
            )}
          </div>
        </div>

        {/* My profile: editable bio, journey, location, photos, interests */}
        {isMe && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--text)' }}>Your profile</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Bio, journey, location, photos & interests — visible to women you connect with</div>
              </div>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => (editing ? (() => { updateDiscoverProfile({ bio: form.bio, fertilityJourney: form.fertilityJourney, location: form.location, clinic: form.clinic, interests: form.interests.split(/[,;]/).map((s) => s.trim()).filter(Boolean), photos: form.photos, coverPhoto: form.coverPhoto }); if (form.birthday) updateAccountProfile({ date_of_birth: form.birthday, birthday: form.birthday }); setEditing(false); })() : setEditing(true))}
                style={{ fontSize: 13, fontWeight: 800, padding: '6px 12px', borderRadius: 999 }}
              >
                {editing ? 'Save' : 'Edit'}
              </button>
            </div>
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Who you are (bio)</label>
                  <textarea
                    value={form.bio}
                    onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    placeholder="A few lines about you and your journey..."
                    rows={3}
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Fertility journey</label>
                  <input
                    type="text"
                    value={form.fertilityJourney}
                    onChange={(e) => setForm((f) => ({ ...f, fertilityJourney: e.target.value }))}
                    placeholder="e.g. IVF · Cycle 2, TTC · 4 months, Stims · Day 8"
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Where you live (city, state)</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder="e.g. Columbus, OH"
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Fertility clinic</label>
                  <input
                    type="text"
                    value={form.clinic}
                    onChange={(e) => setForm((f) => ({ ...f, clinic: e.target.value }))}
                    placeholder="e.g. Ohio Fertility Network"
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Photos</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {(form.photos || []).slice(0, 4).map((url, i) => (
                      <div key={i} style={{ position: 'relative', width: 72, height: 72, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
                        <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))} style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: 999, background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                    {(form.photos || []).length < 4 && (
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        style={{ width: 72, height: 72, borderRadius: 12, border: '2px dashed var(--border)', background: 'var(--sand)', color: 'var(--muted)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        + Add
                      </button>
                    )}
                    <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { const files = Array.from(e.target.files || []); const reader = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); }); Promise.all(files.map(reader)).then((urls) => setForm((f) => ({ ...f, photos: [...(f.photos || []), ...urls].slice(0, 4) }))); e.target.value = ''; }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Cover image URL</label>
                  <input
                    type="text"
                    value={form.coverPhoto}
                    onChange={(e) => setForm((f) => ({ ...f, coverPhoto: e.target.value }))}
                    placeholder="https://... (or leave blank to use profile photo)"
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Interests (comma-separated)</label>
                  <input
                    type="text"
                    value={form.interests}
                    onChange={(e) => setForm((f) => ({ ...f, interests: e.target.value }))}
                    placeholder="e.g. IVF support, TWW, Cycle tracking"
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Birthday</label>
                  <input
                    type="date"
                    value={form.birthday}
                    onChange={(e) => setForm((f) => ({ ...f, birthday: e.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Chapter (your journey stage)</label>
                  <select
                    value={user?.journeyType || 'ivf'}
                    onChange={(e) => updateAccountProfile({ journeyType: e.target.value })}
                    style={{ width: '100%', padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                  >
                    {JOURNEY_OPTIONS.filter((j) => ['ivf', 'ttc', 'pregnancy', 'postpartum', 'loss', 'surrogacy', 'baby'].includes(j.id)).map((j) => (
                      <option key={j.id} value={j.id}>{j.title}</option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div>
                {(myProfile.bio || myProfile.fertilityJourney || myProfile.location || myProfile.clinic || (myProfile.interests && myProfile.interests.length > 0) || (myProfile.photos && myProfile.photos.length > 0)) ? (
                  <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src={myProfile.photos?.[0] || '/images/chapters/fertility.png'}
                        alt="Profile"
                        style={{
                          width: 200,
                          height: 200,
                          borderRadius: 14,
                          objectFit: 'cover',
                          border: '1px solid var(--border)',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {myProfile.bio && <p style={{ fontSize: 15, color: 'var(--text)', lineHeight: 1.65, marginBottom: 14 }}>"{myProfile.bio}"</p>}
                      {myProfile.fertilityJourney && <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}><strong style={{ fontSize: 15 }}>About my journey:</strong> {myProfile.fertilityJourney}</div>}
                      {myProfile.location && <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}><strong style={{ fontSize: 15 }}>Location:</strong> {myProfile.location}</div>}
                      {myProfile.clinic && <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}><strong style={{ fontSize: 15 }}>Clinic:</strong> {myProfile.clinic}</div>}
                      {(user?.date_of_birth || user?.birthday) && <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}><strong style={{ fontSize: 15 }}>Birthday:</strong> {new Date(user.date_of_birth || user.birthday).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
                      <div style={{ fontSize: 15, color: 'var(--muted)', marginBottom: 10 }}><strong style={{ fontSize: 15 }}>Chapter:</strong> {chapterDisplayName(user?.journeyType)}</div>
                      {myProfile.photos && myProfile.photos.length > 1 && (
                        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                          {myProfile.photos.slice(1).map((url, i) => (
                            <img key={i} src={url} alt="" style={{ width: 64, height: 64, borderRadius: 12, objectFit: 'cover', border: '1px solid var(--border)' }} />
                          ))}
                        </div>
                      )}
                      {(myProfile.interests || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {myProfile.interests.map((tag) => (
                            <span key={tag} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 999, background: 'var(--pink-l)', color: 'var(--pink-d)', fontWeight: 600 }}>{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src="/images/chapters/fertility.png"
                        alt="Profile"
                        style={{
                          width: 200,
                          height: 200,
                          borderRadius: 14,
                          objectFit: 'cover',
                          border: '1px solid var(--border)',
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0, padding: 20, background: 'var(--sand)', borderRadius: 12, border: '1px dashed var(--border)' }}>
                      <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 12 }}>Add a bio, your fertility journey, location, photos, and interests so others can get to know you.</p>
                      <button type="button" onClick={() => setEditing(true)} style={{ fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '10px 20px', borderRadius: 999, cursor: 'pointer' }}>Edit profile →</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Discover profile: full sample profile (about, chapter, friends, posts, birthday, rooms) */}
        {discoverProfile && (
          <>
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--text)', marginBottom: 12 }}>About my journey</div>
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, marginBottom: 12 }}>"{discoverProfile.bio}"</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {discoverProfile.journey && <div style={{ fontSize: 13, color: 'var(--muted)' }}><strong>Journey:</strong> {discoverProfile.journey}</div>}
                {discoverProfile.location && <div style={{ fontSize: 13, color: 'var(--muted)' }}><strong>Location:</strong> {discoverProfile.location}</div>}
                {discoverProfile.clinic && <div style={{ fontSize: 13, color: 'var(--muted)' }}><strong>Clinic:</strong> {discoverProfile.clinic}</div>}
                {discoverProfile.birthday && <div style={{ fontSize: 13, color: 'var(--muted)' }}><strong>Birthday:</strong> {new Date(discoverProfile.birthday).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {(discoverProfile.interests || []).map((tag) => (
                  <span key={tag} style={{ fontSize: 12, padding: '6px 12px', borderRadius: 999, background: 'var(--pink-l)', color: 'var(--pink-d)', fontWeight: 600 }}>{tag}</span>
                ))}
              </div>
              {!friends?.some((f) => f.identifier === ident) ? (
                <button type="button" onClick={() => addFriend?.({ identifier: discoverProfile.id, name: discoverProfile.name })} style={{ fontSize: 14, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '12px 24px', borderRadius: 999, cursor: 'pointer' }}>+ Add as friend</button>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--sage-d)', fontWeight: 700 }}>✓ You're friends — <button type="button" onClick={() => navigate('/community')} style={{ background: 'none', border: 'none', color: 'var(--pink)', cursor: 'pointer', fontWeight: 700, padding: 0 }}>Message in Community →</button></div>
              )}
            </div>

            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>Friends</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Preview</div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>Add as friend to see mutual friends and connect.</div>
            </div>

            {(discoverProfile.roomsJoined?.length > 0) && (
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)', marginBottom: 10 }}>Rooms joined</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {discoverProfile.roomsJoined.map((gid) => {
                    const g = COMMUNITIES.find((c) => c.id === gid);
                    return g ? (
                      <div key={g.id} style={{ padding: '8px 12px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--white)', fontSize: 12, color: 'var(--text)', fontWeight: 800 }}>{g.icon} {g.name}</div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>Profile posts</div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{(discoverProfile.posts || []).length} total</div>
              </div>
              {(discoverProfile.posts || []).length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>No posts yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(discoverProfile.posts || []).map((post, i) => (
                    <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', background: 'var(--white)' }}>
                      <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 6 }}>{new Date(post.createdAt).toLocaleString()}</div>
                      <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{post.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Friends + Joined groups — grid for better space usage */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>Friends</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{isMe ? `${friends.length} total` : 'Preview'}</div>
          </div>
          {discoverProfile ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>Connect to see mutual friends and start chatting.</div>
          ) : isMe ? (
            friends.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>No friends yet. Add someone in Connect.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {friends.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => navigate(`/profile/${encodeURIComponent(String(f.identifier))}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: '1px solid var(--border)',
                      background: 'var(--white)',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: '1px solid var(--border)',
                        background: avatarStyleFromIdentifier(f.identifier).bg,
                        color: avatarStyleFromIdentifier(f.identifier).color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 900,
                        flexShrink: 0,
                      }}
                    >
                      {initialsFrom(f.name)}
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: 'var(--text)' }}>{f.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{WOMEN_TO_DISCOVER.find((w) => w.id === f.identifier)?.journey || (f.identifier?.includes('@') ? f.identifier : 'View profile')}</div>
                    </div>
                  </button>
                ))}
              </div>
            )
          ) : (
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              Friend list isn’t available unless you’re signed in on the same device/browser as the profile creator.
            </div>
          )}
        </div>

        {/* Rooms I've joined */}
        {isMe && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)', marginBottom: 10 }}>Joined groups</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {joinedGroupIds.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>No group memberships yet.</div>
              ) : (
                joinedGroupIds
                  .map((gid) => COMMUNITIES.find((g) => g.id === gid))
                  .filter(Boolean)
                  .map((g) => (
                    <div
                      key={g.id}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: '1px solid var(--border)',
                        background: 'var(--white)',
                        fontSize: 12,
                        color: 'var(--text)',
                        fontWeight: 800,
                      }}
                    >
                      {g.icon} {g.name}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}
        </div>

        {/* Profile posts */}
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)' }}>Profile posts</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{posts.length} total</div>
          </div>

          {posts.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>No posts yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {posts.map((post) => (
                <div key={post.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '10px 12px', background: 'var(--white)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--sand)', color: 'var(--muted)' }}>
                        {chapterLabel(post.topic)}
                      </span>
                      {post.groupId && (
                        <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: 'var(--pink-l)', color: 'var(--pink-d)' }}>
                          {COMMUNITIES.find((g) => g.id === post.groupId)?.name || 'Group'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{new Date(post.createdAt).toLocaleString()}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>{post.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account & privacy — email, password (private) */}
        {isMe && (
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>Account & privacy</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Email and password are private — only you can change them.</div>
            {accountError && <div style={{ fontSize: 12, color: 'var(--hotpink)', marginBottom: 12 }}>{accountError}</div>}
            {accountSuccess && <div style={{ fontSize: 12, color: 'var(--sage-d)', marginBottom: 12 }}>{accountSuccess}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Email (private)</label>
                {accountEditing ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      style={{ flex: 1, minWidth: 200, padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                    />
                    <input
                      type="password"
                      value={accountForm.currentPassword}
                      onChange={(e) => setAccountForm((f) => ({ ...f, currentPassword: e.target.value }))}
                      placeholder="Current password"
                      style={{ flex: 1, minWidth: 160, padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        setAccountError('');
                        setAccountSuccess('');
                        const res = await changeEmail(accountForm.email, accountForm.currentPassword);
                        if (res?.ok) {
                          setAccountSuccess('Email updated. You may need to sign in again.');
                          setAccountEditing(false);
                          setAccountForm((f) => ({ ...f, currentPassword: '' }));
                        } else {
                          setAccountError(res?.error || 'Failed to update email.');
                        }
                      }}
                      style={{ fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '10px 18px', borderRadius: 999, cursor: 'pointer' }}
                    >
                      Update email
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, color: 'var(--text)' }}>{user?.email || '—'}</span>
                    <button type="button" onClick={() => setAccountEditing(true)} style={{ fontSize: 12, fontWeight: 600, color: 'var(--pink)', background: 'none', border: 'none', cursor: 'pointer' }}>Change</button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 4 }}>Password (private)</label>
                {accountEditing ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="password"
                      value={accountForm.currentPassword}
                      onChange={(e) => setAccountForm((f) => ({ ...f, currentPassword: e.target.value }))}
                      placeholder="Current password"
                      style={{ flex: 1, minWidth: 140, padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                    />
                    <input
                      type="password"
                      value={accountForm.newPassword}
                      onChange={(e) => setAccountForm((f) => ({ ...f, newPassword: e.target.value }))}
                      placeholder="New password"
                      style={{ flex: 1, minWidth: 140, padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                    />
                    <input
                      type="password"
                      value={accountForm.newPasswordConfirm}
                      onChange={(e) => setAccountForm((f) => ({ ...f, newPasswordConfirm: e.target.value }))}
                      placeholder="Confirm new"
                      style={{ flex: 1, minWidth: 140, padding: 10, borderRadius: 10, border: '1px solid var(--border)', fontSize: 14 }}
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        setAccountError('');
                        setAccountSuccess('');
                        if (accountForm.newPassword !== accountForm.newPasswordConfirm) {
                          setAccountError('New passwords do not match.');
                          return;
                        }
                        if (accountForm.newPassword.length < 8) {
                          setAccountError('Password must be at least 8 characters.');
                          return;
                        }
                        const res = await changePassword(accountForm.currentPassword, accountForm.newPassword);
                        if (res?.ok) {
                          setAccountSuccess('Password updated.');
                          setAccountForm((f) => ({ ...f, currentPassword: '', newPassword: '', newPasswordConfirm: '' }));
                        } else {
                          setAccountError(res?.error || 'Failed to update password.');
                        }
                      }}
                      style={{ fontSize: 13, fontWeight: 700, color: 'white', background: 'var(--pink)', border: 'none', padding: '10px 18px', borderRadius: 999, cursor: 'pointer' }}
                    >
                      Update password
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setAccountEditing(true)} style={{ fontSize: 13, fontWeight: 600, color: 'var(--pink)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Change password →</button>
                )}
              </div>
              {accountEditing && (
                <button type="button" onClick={() => { setAccountEditing(false); setAccountError(''); setAccountSuccess(''); setAccountForm({ email: user?.email || '', currentPassword: '', newPassword: '', newPasswordConfirm: '' }); }} style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', alignSelf: 'flex-start' }}>Cancel</button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardAppShell>
  );
}

